import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { HighlightedActionButtonV2 } from '@/components/common/Button';
import { renderButton } from '@/components/common/Buttons/utils';
import { CtaCard } from '@/components/common/CtaCard';
import { RichHeading } from '@/components/common/Heading';
import { Icon } from '@/components/common/Icon';
import { Image } from '@/components/common/image';
import { Paragraph } from '@/components/common/MarkUp';
import {
  Modal,
  ModalClose,
  ModalContent,
  ModalHeading,
  ModalTrigger,
} from '@/components/common/Modal';
import { PortableText, VisuallyHidden } from '@/components/common/utils';
import { WistiaEmbed } from '@/components/common/WistiaEmbed';
import { getButtonComponentTheme, getTextColour } from '@/lib/colourUtils';
import { getSanityImageUrl } from '@/lib/sanityUtils';

import type { TextColourTokenType } from '@/types/colours';
import type { SanityHeroTextLeftImageRightType } from '@/types/sanity';

export interface HeroTextLeftImageRightProps
  extends SanityHeroTextLeftImageRightType {}

const HeroTextLeftImageRight = ({
  heading,
  subheading,
  bgImageDesktop,
  mobileImage,
  ctaSection,
  wistiaPopover,
  foregroundRightImage,
  bgImageMobile,
  backgroundColour,
}: HeroTextLeftImageRightProps) => {
  const imageForMobile = mobileImage || bgImageDesktop;
  const backgroundColourToken = backgroundColour?.token;
  return (
    <>
      <FullWidthWrapper
        bgImageMobile={bgImageMobile}
        bgImageDesktop={bgImageDesktop}
      >
        <InnerWrapper colour={getTextColour(backgroundColourToken)}>
          <LeftPanel>
            <HeadingWrapper>
              <RichHeading size="headline-lg" heading={heading} />
              {subheading && (
                <Subheading size="body-md">{subheading}</Subheading>
              )}
            </HeadingWrapper>
            {wistiaPopover?.clickableText && (
              <WistiaWrapper>
                <Modal>
                  <ModalTrigger>
                    <StyledHighlightedActionButtonV2
                      as="span"
                      variant="text"
                      size="sm"
                      leftIcon={
                        <Icon
                          icon="PlayCircle"
                          size="15px"
                          aria-hidden="true"
                        />
                      }
                      highlightColour={wistiaPopover.highlightColour?.token}
                      highlightStyle={wistiaPopover.highlightStyle}
                      className={
                        wistiaPopover.trackingTag?.blockName &&
                        `tracking-${wistiaPopover.trackingTag.blockName}__${wistiaPopover.trackingTag.blockAction}`
                      }
                      colour={getTextColour(backgroundColourToken, {
                        action: true,
                      })}
                    >
                      {wistiaPopover.clickableText}
                    </StyledHighlightedActionButtonV2>
                  </ModalTrigger>
                  <ModalContent className="Modal">
                    <ModalClose>
                      <Icon icon="Close" aria-hidden size="10px" />
                    </ModalClose>
                    <ModalHeading>
                      <VisuallyHidden>
                        {wistiaPopover.clickableText}
                      </VisuallyHidden>
                    </ModalHeading>
                    <WistiaEmbed videoId={wistiaPopover.videoId} />
                  </ModalContent>
                </Modal>
              </WistiaWrapper>
            )}
          </LeftPanel>
          <RightPanel
            imageVerticalPosition={
              foregroundRightImage?.verticalPosition ?? 'bottom'
            }
          >
            {!!foregroundRightImage && (
              <Image {...foregroundRightImage} object-fit="contain" />
            )}
          </RightPanel>
        </InnerWrapper>
        <MobileImage>
          <Image {...imageForMobile} />
        </MobileImage>
      </FullWidthWrapper>
      {!!ctaSection && (
        <CTAWrapper>
          <CTAContainer>
            {ctaSection.map((cta) => {
              return (
                <CtaCard
                  key={cta._key}
                  heading={cta.heading}
                  backgroundColour={cta.backgroundColour?.token}
                  body={<PortableText value={cta.body} />}
                  actions={
                    <>
                      {cta.actions.map(
                        ({
                          buttonLabel,
                          buttonVariant,
                          buttonLink,
                          buttonAccessibleLabel,
                          trackingTag,
                        }) => {
                          const commonProps = {
                            key: buttonLabel,
                            iconPosition: 'right' as const,
                            href: buttonLink,
                            componentTheme: getButtonComponentTheme(
                              cta.backgroundColour?.token ||
                                '--surface-warm-base',
                            ),
                            'aria-label': buttonAccessibleLabel,
                            className:
                              trackingTag?.blockName &&
                              `tracking-${trackingTag.blockName}__${trackingTag.blockAction}`,
                            children: buttonLabel,
                          };
                          return renderButton(buttonVariant, commonProps);
                        },
                      )}
                    </>
                  }
                />
              );
            })}
          </CTAContainer>
        </CTAWrapper>
      )}
    </>
  );
};

const heroBackgroundImage = (bgImage) => {
  const heroBG = getSanityImageUrl(bgImage);

  if (!heroBG) return '';

  return css`
    background-color: var(--background-primary);
    background-image: url(${heroBG});
    background-repeat: no-repeat;
    background-position: 50%;
    background-size: cover;
  `;
};

const FullWidthWrapper = styled.div<{ bgImageMobile; bgImageDesktop }>`
  ${(props) => props.bgImageMobile && heroBackgroundImage(props.bgImageMobile)}

  ${screen.md} {
    position: relative;

    ${(props) =>
      props.bgImageDesktop && heroBackgroundImage(props.bgImageDesktop)}

    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 600px;
  }

  ${screen.lg} {
    min-height: 626px;
  }
`;

const HeadingWrapper = styled.div`
  padding-top: calc(65px + var(--spacing-small));

  ${screen.sm} {
    padding-top: calc(65px + var(--spacing-300));
  }

  ${screen.md} {
    max-width: 480px;
    padding-top: calc(var(--full-navbar-height) + var(--spacing-600));
  }

  ${screen.lg} {
    max-width: 560px;
    padding-top: calc(var(--full-navbar-height) + var(--spacing-900));
  }

  font-kerning: none;
`;

const Subheading = styled(Paragraph)`
  margin-top: var(--spacing-x-small);
  margin-bottom: var(--spacing-small);

  ${screen.sm} {
    margin-bottom: var(--spacing-300);
  }

  ${screen.md} {
    max-width: 400px;
  }
`;

const WistiaWrapper = styled.div`
  margin-bottom: var(--spacing-500);

  *:first-child {
    padding-left: 0;
  }

  ${screen.md} {
    margin-bottom: var(--spacing-800);
  }
`;

const MobileImage = styled.div`
  ${screen.md} {
    display: none;
  }
`;

const CTAWrapper = styled.div`
  width: 100%;
  position: relative;
  margin: -4.5rem auto 0 auto;
  padding: var(--spacing-400) var(--spacing-250) var(--spacing-400);
  overflow: hidden;

  ${screen.md} {
    padding-left: 0;
    padding-right: 0;
    margin-top: -10.6rem;
  }

  @media (min-width: 1920px) {
    overflow: visible;
  }
`;

const CTAContainer = styled.div`
  height: auto;
  margin: 0 auto;
  max-width: 820px;
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  flex-wrap: wrap;
  gap: var(--spacing-small);
  z-index: 1;

  ${screen.sm} {
    gap: var(--spacing-300);
  }

  ${screen.md} {
    margin: 0 auto;
    max-width: 1440px;
    padding: 0 var(--section-gutter);
    gap: var(--spacing-400);
    grid-template-columns: 1fr 1fr;
  }

  ${screen.lg} {
    gap: var(--spacing-500);
  }
`;

const InnerWrapper = styled.div<{
  colour: TextColourTokenType;
}>`
  margin: 0 auto;
  width: 100%;
  max-width: 1440px;
  padding: 0 var(--section-gutter);
  display: flex;
  flex-grow: 1;
  ${(p) => `color: var(${p.colour})`}
`;

const LeftPanel = styled.div`
  flex-grow: 1;

  ${screen.md} {
    flex: 473px;
  }

  ${screen.lg} {
    flex: 576px;
  }
`;

const RightPanel = styled.div<{ imageVerticalPosition: string }>`
  display: none;

  ${screen.md} {
    flex: 50%;
    display: flex;
    flex-grow: 0;
    flex-shrink: 1;
    padding-top: ${(p) =>
      p.imageVerticalPosition === 'bottom' ? '6rem' : '0'};
    flex-direction: column;
    justify-content: ${(p) => {
      switch (p.imageVerticalPosition) {
        case 'top':
          return `flex-start`;
        case 'middle':
          return `center`;
        case 'bottom':
        default:
          return `flex-end`;
      }
    }};
  }
`;

const StyledHighlightedActionButtonV2 = styled(HighlightedActionButtonV2)<{
  colour: TextColourTokenType;
}>`
  ${(p) => `color: var(${p.colour})`}
`;

export { HeroTextLeftImageRight };
